import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  DOCUMENT_TYPES,
  KYC_DOCUMENT_TYPES,
  KYC_PERSON_TARGET_TYPES,
} from '../../util/constants';
import KycLink from './KycLink';
import { PAGES } from '../../constants/pages';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import DropzoneArea from '../../components/DropzoneArea';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import { socureUploadDocument } from '../../store/actions/documents/documentsActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import { useApplicantData } from '../../hooks/useApplicantData';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const DropZoneWrapper = styled.div`
  max-width: 25rem;
  width: 100%;

  > div {
    > div {
      width: 100%;
    }
  }
`;

const KycUploadResidencyDocument = ({ documentType, applicantType }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { onSuccess: onSuccessSkip } = useStepPost({
    nextPath: PAGES.KYC_DONE_SKIP,
  });
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const selectedDocument = retrieveFromSessionStorage(KYC_SELECTED_OPTION);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState({
    Document: '',
  });
  const { BrokerName } = useSelector(selectSettings);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc Residency',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc Residency',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  // eslint-disable-next-line no-console

  const onSubmit = () => {
    const formData = new FormData();
    formData.append('Document', uploadedDocuments.Document);
    formData.append('Caption', selectedDocument?.Caption);

    const onSuccess = () => {
      history.push(PAGES.KYC_VERIFICATION);
      handleGtmEvent();
    };
    const params = {
      personTargetType:
        KYC_PERSON_TARGET_TYPES[selectedDocument?.PersonTargetType],
      documentType: KYC_DOCUMENT_TYPES[`${selectedDocument?.DocumentType}`],
    };

    dispatch(
      socureUploadDocument({
        ApplicationUid,
        params,
        data: formData,
        onSuccess,
        setErrors,
      }),
    );
  };

  const getTitleTag = () => {
    switch (documentType) {
      case DOCUMENT_TYPES.CREDIT_CARD:
        return t('kycVerification.titleTagCreditCardStatement', {
          brokerName: BrokerName,
        });
      case DOCUMENT_TYPES.UTILITY_BILL:
        return t('kycVerification.titleTagUtilityBill', {
          brokerName: BrokerName,
        });
      case DOCUMENT_TYPES.BANK_STATEMENT:
        return t('kycVerification.titleTagBankStatement', {
          brokerName: BrokerName,
        });
      case DOCUMENT_TYPES.VOIDED_CHECK:
        return t('kycVerification.titleTagVoidedCheck', {
          brokerName: BrokerName,
        });
      default:
        break;
    }
  };

  const getHeaderContent = () => {
    switch (documentType) {
      case DOCUMENT_TYPES.CREDIT_CARD:
        return {
          title: t('kycVerification.ccTitle'),
          subtitle: t('kycVerification.ccSubtitle'),
        };

      case DOCUMENT_TYPES.UTILITY_BILL:
        return {
          title: t('kycVerification.utilTitle'),
          subtitle: t('kycVerification.utilSubtitle'),
        };

      case DOCUMENT_TYPES.BANK_STATEMENT:
        return {
          title: t('kycVerification.bankStatementTitle'),
          subtitle: t('kycVerification.bankStatementSubtitle'),
        };
      case DOCUMENT_TYPES.VOIDED_CHECK:
        return {
          title: t('kycVerification.vcTitle'),
          subtitle: t('kycVerification.vcSubtitle'),
        };

      default:
        break;
    }
  };

  const headerContent = getHeaderContent();
  const getPreviousPath = () =>
    PAGES.KYC_PROOF_OF_RESIDENCE[String(applicantType).toUpperCase()].MAIN;

  return (
    <BaseLayout previousPath={getPreviousPath()} isKycProcess="true" noLogoBox>
      <Helmet>
        <title>{getTitleTag()}</title>
      </Helmet>
      <Header
        title={headerContent.title}
        subtitle={headerContent.subtitle}
        subTitleMb={24}
      />
      <DropZoneWrapper>
        <DropzoneArea
          onUpload={(file) =>
            setUploadedDocuments({
              ...uploadedDocuments,
              Document: file,
            })
          }
        />
      </DropZoneWrapper>
      <Paragraph color={themeColors.colorErrorMessage} variant="normal">
        {errors}
      </Paragraph>
      <OnBoardingCta
        disabled={!uploadedDocuments.Document}
        onClick={onSubmit}
      />
      <KycLink onClick={onSuccessSkip} />
    </BaseLayout>
  );
};

KycUploadResidencyDocument.propTypes = {
  documentType: PropTypes.string,
  applicantType: PropTypes.string,
};

export default KycUploadResidencyDocument;
